import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  useCreateRelatedEntityMutation,
  useCreateRelatedEntityDraftMutation,
  useUpdateRelatedEntityMutation,
  useUpdateRelatedEntityDraftMutation,
  useCreateBusinessRelationMutation,
  useCreateBusinessRelationDraftMutation,
  useUpdateBusinessRelationMutation,
  useUpdateBusinessRelationDraftMutation,
  useCreateCompanyMutation,
} from "../../../../store/Company/companyApiSlice";
import useCompanyUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import { resultName } from "./ownershipSearch";
import {
  needsCompanyRecord,
  relationCompanyName,
  relationPayload,
  relationSavePath,
  validateRelation,
} from "./relationFields";

/* The relation dialog's behaviour — one implementation for both sections. */
export const useRelationForm = (spec, { editing, chosen, onSaved }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [relationType, setRelationType] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const [createEntity] = useCreateRelatedEntityMutation();
  const [createEntityDraft] = useCreateRelatedEntityDraftMutation();
  const [updateEntity] = useUpdateRelatedEntityMutation();
  const [updateEntityDraft] = useUpdateRelatedEntityDraftMutation();
  const [createBusiness] = useCreateBusinessRelationMutation();
  const [createBusinessDraft] = useCreateBusinessRelationDraftMutation();
  const [updateBusiness] = useUpdateBusinessRelationMutation();
  const [updateBusinessDraft] = useUpdateBusinessRelationDraftMutation();
  const [createCompany] = useCreateCompanyMutation();

  const handleUpdateCompanyFollow = useCompanyUpdate();

  const isEntity = spec.key === "entity";
  const api = isEntity
    ? {
        create: createEntity,
        createDraft: createEntityDraft,
        update: updateEntity,
        updateDraft: updateEntityDraft,
      }
    : {
        create: createBusiness,
        createDraft: createBusinessDraft,
        update: updateBusiness,
        updateDraft: updateBusinessDraft,
      };

  /* Transcribed: an existing row seeds the three editable fields. Its company
     is fixed — the live form renders that field read-only when editing. */
  useEffect(() => {
    if (!editing) return;
    setRelationType(editing.relation_type || "");
    setState(editing.state || "");
    setCountry(editing.country || "");
  }, [editing]);

  /* A business chosen from the search seeds the country, as the ownership
     dialog does. */
  useEffect(() => {
    if (!chosen) return;
    setCountry(chosen.country_code || chosen.country || "");
    if (chosen.state) setState(chosen.state);
  }, [chosen]);

  /* The company on the row being edited, or the one just picked. */
  const companyName = editing ? relationCompanyName(editing) : resultName(chosen);
  const companyLocked = Boolean(editing);

  const submit = async () => {
    const found = validateRelation({
      company: companyName,
      relationType,
      state,
      country,
    });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const path = relationSavePath(
      { verifiedCompany: currentEditingCompany?.verifiedCompany, editing },
      spec
    );

    setBusy(true);
    try {
      /* Transcribed: a WorldBox-only business becomes a TREVEX company first,
         so the relation can point at a real id. */
      let relatedCompanyID =
        editing?.companyID?.companyID || chosen?.trevex_id || chosen?.companyID || "";

      if (needsCompanyRecord(chosen)) {
        const created = await createCompany({
          data: {
            wb_win: chosen.win || chosen.wb_win,
            registered_company_name: resultName(chosen),
            country_code: country,
          },
          token,
        }).unwrap();
        relatedCompanyID = created?.data?.companyID || relatedCompanyID;
      }

      const payload = relationPayload(
        {
          companyID: relatedCompanyID,
          relationType,
          state,
          country,
          companyName,
        },
        spec
      );

      if (path === "updateDraft") {
        await api.updateDraft({
          [spec.idArg]: editing[spec.draftIdKey],
          data: {
            current_companyID: companyID,
            ...payload,
            ...(spec.updateRequestType ? { request_type: spec.updateRequestType } : {}),
            /* Transcribed: carried only when the draft amends a live row. */
            ...(editing?.[spec.draftLinkKey] != null
              ? { [spec.idKey]: editing[spec.idKey] }
              : {}),
          },
          token,
        }).unwrap();
      } else if (path === "createDraft") {
        await api.createDraft({
          data: {
            current_companyID: companyID,
            ...payload,
            ...(spec.createRequestType ? { request_type: spec.createRequestType } : {}),
          },
          token,
        }).unwrap();
      } else if (path === "update") {
        const res = await api.update({
          [spec.idArg]: editing[spec.idKey],
          data: {
            current_companyID: companyID,
            /* Transcribed: the update sends the row's OWN stored company name
               under `company_name`, because that field is read-only here. */
            company_name: relationCompanyName(editing),
            ...payload,
          },
          token,
        }).unwrap();

        if (res) {
          const result = await handleUpdateCompanyFollow(
            companyID,
            spec.followUpdated,
            token
          );
          if (!result.success) console.error(result.error);
        }
      } else {
        const res = await api.create({
          data: { current_companyID: companyID, ...payload },
          token,
        }).unwrap();

        if (res) {
          const result = await handleUpdateCompanyFollow(
            companyID,
            spec.followCreated,
            token
          );
          if (!result.success) console.error(result.error);
        }
      }

      onSaved?.();
    } catch (error) {
      /* The live forms swallow these, so a failed save closed the dialog as
         though it had worked. See FINDINGS F-040. */
      console.error(`${spec.title} save failed:`, error);
      setErrors({
        form:
          error?.data?.detail ||
          error?.data?.message ||
          "Couldn't save this relation. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return {
    companyName,
    companyLocked,
    relationType,
    setRelationType,
    state,
    setState,
    country,
    setCountry,
    errors,
    setErrors,
    busy,
    submit,
  };
};

export default useRelationForm;
